import { ChipProps } from "@mui/material";

import { EXCEL_HEADERS, ORDER_STATUSES } from "constants/index";
import { Order, OrderStatus } from "types";

import { normalizeCellString, toNumber } from "./index";

export const ORDER_STATUS_OPTIONS: Array<{ id: OrderStatus; label: string }> = [
  { id: ORDER_STATUSES.INGRESADO, label: "Ingresado" },
  { id: ORDER_STATUSES.EN_PREPARACION, label: "En preparación" },
  { id: ORDER_STATUSES.PREPARADO, label: "Preparado" },
  { id: ORDER_STATUSES.DESPACHADO, label: "Despachado" },
  { id: ORDER_STATUSES.ENTREGADO, label: "Entregado" },
  { id: ORDER_STATUSES.DETENIDO, label: "Detenido" },
  { id: ORDER_STATUSES.CANCELADO, label: "Cancelado" },
];

export const getOrderStatusChipColor = (
  status?: OrderStatus,
): ChipProps["color"] => {
  switch (status) {
    case ORDER_STATUSES.INGRESADO:
      return "default";
    case ORDER_STATUSES.EN_PREPARACION:
      return "warning";
    case ORDER_STATUSES.PREPARADO:
      return "success";
    case ORDER_STATUSES.DESPACHADO:
      return "info";
    case ORDER_STATUSES.ENTREGADO:
      return "secondary";
    case ORDER_STATUSES.DETENIDO:
    case ORDER_STATUSES.CANCELADO:
      return "error";
    default:
      return "default";
  }
};

export const getOrderStatusLabel = (status?: OrderStatus) =>
  ORDER_STATUS_OPTIONS.find((option) => option.id === status)?.label ?? "-";

export const buildOrderErrorMessage = (
  data: any,
  fallbackMessage = "No se pudo procesar la accion sobre los pedidos.",
) => {
  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message;
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors
      .map((error: { message?: string }) => error.message)
      .filter(Boolean)
      .join(" ");
  }

  return fallbackMessage;
};

export const canStopOrder = (order: Order) => {
  const status = order.movement_order?.order_status;

  return (
    status != null &&
    ([ORDER_STATUSES.INGRESADO, ORDER_STATUSES.EN_PREPARACION] as OrderStatus[]).includes(
      status,
    )
  );
};

export const canCancelOrder = (order: Order) => {
  const status = order.movement_order?.order_status;

  return (
    status != null &&
    ([
      ORDER_STATUSES.INGRESADO,
      ORDER_STATUSES.EN_PREPARACION,
      ORDER_STATUSES.PREPARADO,
      ORDER_STATUSES.DETENIDO,
      ORDER_STATUSES.DESPACHADO,
      ORDER_STATUSES.ENTREGADO,
    ] as OrderStatus[]).includes(status)
  );
};

export const canResumeOrder = (order: Order) =>
  order.movement_order?.order_status === ORDER_STATUSES.DETENIDO &&
  Boolean(order.movement_order?.previous_order_status);

export const canDispatchOrder = (order: Order) =>
  order.movement_order?.order_status === ORDER_STATUSES.PREPARADO;

export const canEditOrder = (order: Order) => {
  const status = order.movement_order?.order_status;

  return (
    status != null &&
    ([
      ORDER_STATUSES.INGRESADO,
      ORDER_STATUSES.EN_PREPARACION,
      ORDER_STATUSES.PREPARADO,
    ] as OrderStatus[]).includes(status)
  );
};

export const buildOrderImportHeaderData = (row: Record<string, unknown>) => {
  const payload: Record<string, unknown> = {};

  const documentoDelCliente = normalizeCellString(
    row[EXCEL_HEADERS.CABECERA.DOCUMENTO_DEL_CLIENTE] ??
      row[EXCEL_HEADERS.ORDERS_IMPORT.DOCUMENTO_DEL_CLIENTE],
  );
  const usuarioExcel = normalizeCellString(
    row[EXCEL_HEADERS.CABECERA.CREATED_BY],
  );
  const preparoExcel = normalizeCellString(
    row[EXCEL_HEADERS.CABECERA.UPDATED_BY],
  );
  const palletsExcel = toNumber(
    (row[EXCEL_HEADERS.CABECERA.PALLETS] ?? 0) as string | number,
  );
  const pesoExcel = toNumber(
    (row[EXCEL_HEADERS.CABECERA.PESO] ?? 0) as string | number,
  );

  if (documentoDelCliente) payload.documento_del_cliente = documentoDelCliente;
  if (usuarioExcel) payload.usuario_excel = usuarioExcel;
  if (preparoExcel) payload.preparo_excel = preparoExcel;
  if (palletsExcel > 0) payload.pallets_excel = palletsExcel;
  if (pesoExcel > 0) payload.peso_excel = pesoExcel;

  return Object.keys(payload).length > 0 ? payload : null;
};
